/**
 * String Prototype Extensions
 *
 * Extends the String prototype with utility methods for parameter replacement.
 * Supports both positional and named parameter replacement patterns.
 *
 * Usage:
 * - Positional: 'Hello {0}, you have {1} messages'.replaceParam('John', 5)
 * - Named: 'Hello {name}, you have {count} messages'.replaceParam({name: 'John', count: 5})
 *
 * @example
 * ```typescript
 * // Positional parameters
 * '/api/users/{0}/posts/{1}'.replaceParam('john_doe', 123)
 * // Result: '/api/users/john_doe/posts/123'
 *
 * // Named parameters
 * '/api/users/{username}/posts/{id}'.replaceParam({username: 'john_doe', id: 123})
 * // Result: '/api/users/john_doe/posts/123'
 *
 * // Mixed usage
 * 'Welcome {name}, you have {count} notifications'.replaceParam('Alice', 3)
 * // Result: 'Welcome Alice, you have 3 notifications'
 * ```
 */

/**
 * Type definition for named parameters
 */
export type NamedParams = Record<string, string | number>;

/**
 * Type definition for positional parameters
 */
export type PositionalParams = (string | number)[];

/**
 * Union type for all parameter types
 */
export type StringParams = NamedParams | PositionalParams;

/**
 * Extends String prototype with replaceParam method
 *
 * @param params - Either an object with named parameters or an array of positional parameters
 * @returns The string with parameters replaced
 */
declare global {
  interface String {
    replaceParam(...params: PositionalParams): string;
    replaceParam(params: NamedParams): string;
  }
}

/**
 * Implementation of the replaceParam method
 */
if (!String.prototype.replaceParam) {
  String.prototype.replaceParam = function (
    paramsOrFirst: StringParams | string | number,
    ...restParams: (string | number)[]
  ): string {
    let str = this.toString();

    // Handle named parameters (object)
    if (typeof paramsOrFirst === 'object' && !Array.isArray(paramsOrFirst)) {
      const namedParams = paramsOrFirst as NamedParams;

      // Replace named parameters like {key}
      for (const [key, value] of Object.entries(namedParams)) {
        const placeholder = `{${key}}`;
        str = str.replace(
          new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
          String(value)
        );
      }

      return str;
    }

    // Handle positional parameters (array or individual arguments)
    const positionalParams = Array.isArray(paramsOrFirst)
      ? paramsOrFirst
      : [paramsOrFirst, ...restParams];

    // Replace positional parameters like {0}, {1}, etc.
    positionalParams.forEach((param, index) => {
      // Only replace if parameter is not undefined
      if (param !== undefined) {
        const placeholder = `{${index}}`;
        str = str.replace(
          new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
          String(param)
        );
      }
    });

    return str;
  };
}

/**
 * Utility function to create parameterized strings
 *
 * @param template - The string template with placeholders
 * @param params - Parameters to replace
 * @returns The string with parameters replaced
 *
 * @example
 * ```typescript
 * // Using the utility function
 * const result = replaceParams('/api/users/{username}/posts/{id}', {
 *   username: 'john_doe',
 *   id: 123
 * });
 * // Result: '/api/users/john_doe/posts/123'
 * ```
 */
export function replaceParams(template: string, params: StringParams): string {
  if (isNamedParams(params)) {
    return template.replaceParam(params);
  } else {
    return template.replaceParam(...params);
  }
}

/**
 * Utility function to create parameterized strings with positional parameters
 *
 * @param template - The string template with placeholders
 * @param params - Positional parameters to replace
 * @returns The string with parameters replaced
 *
 * @example
 * ```typescript
 * // Using the utility function with positional params
 * const result = replaceParamsPositional('/api/users/{0}/posts/{1}', 'john_doe', 123);
 * // Result: '/api/users/john_doe/posts/123'
 * ```
 */
export function replaceParamsPositional(
  template: string,
  ...params: (string | number)[]
): string {
  return template.replaceParam(...params);
}

/**
 * Utility function to create parameterized strings with named parameters
 *
 * @param template - The string template with placeholders
 * @param params - Named parameters to replace
 * @returns The string with parameters replaced
 *
 * @example
 * ```typescript
 * // Using the utility function with named params
 * const result = replaceParamsNamed('/api/users/{username}/posts/{id}', {
 *   username: 'john_doe',
 *   id: 123
 * });
 * // Result: '/api/users/john_doe/posts/123'
 * ```
 */
export function replaceParamsNamed(
  template: string,
  params: NamedParams
): string {
  return template.replaceParam(params);
}

/**
 * Type guard to check if parameters are named
 */
export function isNamedParams(params: StringParams): params is NamedParams {
  return typeof params === 'object' && !Array.isArray(params);
}

/**
 * Type guard to check if parameters are positional
 */
export function isPositionalParams(
  params: StringParams
): params is PositionalParams {
  return Array.isArray(params);
}

/**
 * Utility function to capitalize the first letter of a string
 *
 * @param str string
 * @returns string
 */
export function ucfirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Title-case a string: capitalize the first letter of each word and lowercase
 * the rest. Internal/leading/trailing whitespace is preserved. Mirrors the
 * backend's `Str::title()` so values round-trip consistently.
 *
 * @param str string
 * @returns string
 *
 * @example
 * toTitleCase('upper arms')           // 'Upper Arms'
 * toTitleCase('cardiovascular system') // 'Cardiovascular System'
 */
export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export default {
  replaceParams,
  replaceParamsPositional,
  replaceParamsNamed,
  isNamedParams,
  isPositionalParams,
};
